"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleQuestionMark as HelpCircle, MessageSquare, ArrowRight, Sparkles, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const faqList = [{
  id: "item-1",
  question: "Why is targeted Local SEO essential for Sunderland and Wearside businesses?",
  answer: "Sunderland has a vibrant, highly competitive local economy across retail, professional services, trades, and industrial sectors. When customers in Wearside search for your service, Google prioritises the top 3 Local Map Pack results and targeted organic listings. My tailored local SEO strategies ensure your business ranks prominently when local clients are ready to buy, bringing high-intent traffic directly to you."
}, {
  id: "item-2",
  question: "Will I work directly with you, Yassine, or will my account be passed to a junior?",
  answer: "You will work directly with me. Unlike traditional marketing agencies that assign accounts to junior account managers, I personally handle every audit, local strategy, Google Business Profile optimisation, and technical tweak. You get my direct mobile number, clear monthly video updates, and honest expert advice."
}, {
  id: "item-3",
  question: "How quickly can a Sunderland business expect tangible local search results?",
  answer: "While full organic campaign momentum takes 3 to 6 months, local SEO quick wins—such as Google Business Profile optimisation, fixing duplicate NAP (Name, Address, Phone) listings, and correcting local indexing issues—often produce visible improvements in local Map Pack visibility within 30 to 60 days."
}, {
  id: "item-4",
  question: "How do you optimize Google Business Profiles for Sunderland trade and service area businesses?",
  answer: "I systematically audit and refine your profile: selecting primary and secondary categories tailored to Sunderland search volume, crafting geo-relevant service descriptions, building local photo assets, setting up dedicated booking links, and implementing a structured strategy for acquiring verified customer reviews."
}, {
  id: "item-5",
  question: "What is the key difference between local SEO in Sunderland and a UK-wide campaign?",
  answer: "Local SEO focuses heavily on proximity, Google Map Pack rankings, localized landing pages, geographic citations, and local customer intent in Wearside and surrounding North East regions. National SEO focuses on broader topic authority and higher volume non-location keywords. Local SEO converts faster because searchers usually need immediate local service."
}, {
  id: "item-6",
  question: "Do you guarantee #1 rankings on Google Maps in Tyne and Wear?",
  answer: "No honest SEO consultant guarantees #1 rankings, as Google's local algorithms constantly update. However, I focus on proven, white-hat local search strategies that systematically eliminate technical errors, build local domain authority, and outperform local competitors sustainably."
}, {
  id: "item-7",
  question: "Can local SEO help my business if I don't have a physical retail shopfront in Sunderland?",
  answer: "Yes, absolutely. Service Area Businesses (SABs)—such as plumbers, legal consultants, mobile contractors, and B2B specialists operating across Sunderland, Washington, and Seaham—can rank prominently in local maps by configuring proper service areas without displaying a private residential address."
}, {
  id: "item-8",
  question: "How do local citations and directory consistency impact my Wearside rankings?",
  answer: "Google cross-references business data across the web. If your company name, address, or phone number varies across UK directories, Google loses trust in your location signals. I clean up inconsistent citations and build high-quality local links to solidify your geographic footprint."
}, {
  id: "item-9",
  question: "What transparent reporting metrics will I receive each month?",
  answer: "I provide clear, fluff-free reports focusing on business outcomes: local map pack position tracking, organic Sunderland traffic growth, phone call clicks, direction requests, and contact form enquiries. I explain what was done, what changed, and what comes next in plain English."
}, {
  id: "item-10",
  question: "How does modern AI Search (Gemini & ChatGPT) change local search in Sunderland?",
  answer: "AI search engines extract local business details directly from verified entity records, structured schema markup, and online customer sentiment. My local SEO campaigns optimize for both traditional Google search and conversational AI answers, ensuring your Sunderland brand remains visible everywhere."
}, {
  id: "item-11",
  question: "What does your initial Sunderland Local SEO audit involve?",
  answer: "I perform a comprehensive analysis covering your current Google Maps placement, local citation consistency, website technical health, localized content relevancy, competitor benchmarking in Sunderland, and conversion rate bottlenecks, giving you a clear prioritized roadmap."
}, {
  id: "item-12",
  question: "How do I get started with a free local SEO consultation?",
  answer: "Simply click 'Book a Free SEO Consultation' to schedule a 30-minute discovery call with me. I'll take a look at your current website and local visibility beforehand so we can discuss actionable recommendations tailored specifically to your Sunderland market goals."
}];
const highlights = ["Direct Consultant Access", "Tailored Sunderland Strategy", "Transparent Monthly Reporting", "No Retainer Trap Contracts"];
export default function Faq() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="localseofaq" data-nav="light" className="relative py-16 md:py-24 bg-background text-foreground overflow-hidden">
      {}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-secondary/60 rounded-full blur-3xl pointer-events-none -z-10 opacity-50" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
        {}
        <div className="text-center max-w-3xl mx-auto mb-12 lg:mb-16">
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 16
        }} whileInView={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }} className="flex items-center justify-center gap-2 mb-4">
            <Badge variant="secondary" className="px-3 py-1 text-xs font-semibold uppercase tracking-wider gap-1.5 border border-border">
              <HelpCircle className="size-3.5 text-primary" />
              Sunderland Local SEO FAQ
            </Badge>
          </motion.div>

          <motion.h2 initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 20
        }} whileInView={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          ease: "easeOut",
          delay: 0.1
        }} className="text-3xl sm:text-4xl lg:text-5xl font-bold font-default tracking-tight text-foreground">
            Local SEO in Sunderland: <span className="text-primary">Questions Answered</span>
          </motion.h2>

          <motion.p initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 20
        }} whileInView={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          ease: "easeOut",
          delay: 0.2
        }} className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed font-default">
            Clear, honest answers about growing your business search rankings in Wearside. 
            No agency double-talk or buzzwords—just practical local search expertise directly from me, Yassine Essaadi.
          </motion.p>

          {}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 16
        }} whileInView={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          ease: "easeOut",
          delay: 0.3
        }} className="mt-6 flex flex-wrap justify-center gap-3 sm:gap-4">
            {highlights.map((item, idx) => <div key={item} data-index={idx} className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-medium text-foreground/80 bg-muted/60 px-3 py-1.5 rounded-full border border-border/50">
                <CheckCircle2 className="size-3.5 text-primary" />
                <span>{item}</span>
              </div>)}
          </motion.div>
        </div>

        {}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 24
      }} whileInView={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        ease: "easeOut",
        delay: 0.2
      }} className="bg-card rounded-2xl border border-border p-4 sm:p-6 lg:p-8 shadow-sm">
          <Accordion type="single" collapsible className="w-full space-y-2">
            {faqList.map((faq, index) => <AccordionItem key={faq.id} value={faq.id} data-index={index} className="border-b border-border/60 last:border-b-0 px-2 sm:px-4 py-1">
                <AccordionTrigger className="text-left text-base sm:text-lg font-semibold text-primary hover:no-underline py-4 transition-colors">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-sm sm:text-base leading-relaxed pb-5 pt-1 font-default">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </motion.div>

        {}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 24
      }} whileInView={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        ease: "easeOut",
        delay: 0.3
      }} className="mt-12 bg-secondary/40 border border-border rounded-2xl p-6 sm:p-8 text-center flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="text-left max-w-xl">
            <div className="inline-flex items-center gap-2 text-xs font-semibold text-primary uppercase tracking-wider mb-2">
              <Sparkles className="size-4" />
              <span>Direct Expert Consultation</span>
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-foreground">
              Have a specific question about your Sunderland business?
            </h3>
            <p className="mt-1 text-sm sm:text-base text-muted-foreground">
              Book a quick 30-minute discovery call directly with me. I will review your local map presence and give you honest, actionable feedback.
            </p>
          </div>

          <div className="shrink-0 flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <Button asChild size="lg" className="font-medium shadow-sm">
              <Link to="/ContactPage">
                Book a Free SEO Consultation
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="font-medium">
              <Link to="/CaseStudiesPage">
                <MessageSquare className="size-4 mr-1" />
                See Case Studies
              </Link>
            </Button>
          </div>
        </motion.div>

        {}
        <Separator className="mt-16 bg-border/60" />
      </div>
    </section>;
}